import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import {useNavigate, useLocation} from "react-router-dom";
import {
  FormEvent,
  useState
} from "react";
import { Box, Paper, Typography, Grid, TextField, Button, useTheme, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import AuthLayout from '../layouts/AuthLayout/AuthLayout';
import { Link } from 'react-router-dom';
import ErrorDialog from "../../../components/ErrorDialog";
import { useAuthContext } from '../contexts/AuthContext';


const FinishResetDialog = (props: any) => {
    const { open } = props;

    let navigate = useNavigate();
  
    const handleClose = () => {
        navigate({
            pathname: "../login"
        });
    };
  
    return (
        <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        >
        <DialogTitle id="alert-dialog-title">
            Password reset successfully.
        </DialogTitle>
        <DialogContent>
            <DialogContentText id="alert-dialog-description">
                return to login page
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleClose} autoFocus>
            OK
            </Button>
        </DialogActions>
        </Dialog>
      );
  };

function ResetPassword() {
  let navigate = useNavigate();
  let location = useLocation();
  let auth = useAuthContext();
  let theme = useTheme();

  const [error, setError] = useState<string | null>(null);
  const [codeIsSent, setCodeIsSent] = useState<boolean>(false);
  const [activeEmail, setActiveEmail] = useState<string>("");
  const [displayFinishAlert, setDisplayFinishAlert] = useState<boolean>(false);
  const [pageError, setPageError] = useState<string>("");

  function handleSubmit(e: FormEvent<HTMLFormElement>){
    e.preventDefault();

    let formData = new FormData(e.currentTarget);

    if (!codeIsSent) {
        let email = formData.get("email") as string;

        if (email.length < 1) {
            setPageError("must enter an email");
            return;
        }

        auth.forgotPassword(email, (result: any) => {
            if(result.success) {
                setActiveEmail(email);
                setCodeIsSent(true);
            } else {
                let err = result.data.err.message;
                if (err) {
                    setError(err);
                } else {
                    setError("Failed to request reset code, please try again.");
                }
            }
        });
    } else {
        let passcode = formData.get("passcode") as string;
        let newPassword = formData.get("newPassword") as string;
        let passwordConfirmation = formData.get("passwordConfirmation") as string;

        if (!activeEmail || activeEmail.length < 1) {
            setPageError("No email entered, please try again");
            setCodeIsSent(false);
            return;
        }

        if (passcode.length < 1) {
            setPageError("Must enter passcode you recieved in your email!");
            return;
        }

        if (newPassword.length < 1) {
            setPageError("Must enter a new password!");
            return;
        }

        if (newPassword != passwordConfirmation) {
            setPageError("Both passwords must match!");
            return;
        }

        auth.confirmPassword(activeEmail, passcode, newPassword, (result: any) => {
            if(result.success) {
                console.log("successfully changed password");
                setDisplayFinishAlert(true);
            } else {
                let err = result.data.err.message;
                if (err) {
                    setPageError(err);
                } else {
                    setPageError("Failed to change password, please try again.");
                }
            }
        });
    }
  }

  function resendVerificationCode() {

    let email = activeEmail;

    if (email == null || email.length < 1) {
        setPageError("Please re-enter email");
        setCodeIsSent(false);
        return;
    }

    auth.forgotPassword(email, (result: any) => {
        if(result.success) {
            setActiveEmail(email);
            setCodeIsSent(true);
        } else {
            let err = result.data.err.message;
            if (err) {
                setError(err);
            } else {
                setError("Failed to request reset code, please try again.");
            }
        }
    });
  }

  function getTitle(): string {
    if (!codeIsSent) {
        return "Reset Password";
    } else {
        return "Choose New Password";
    }
  }

  function getInputForm(): JSX.Element {
    if (!codeIsSent) {
        return <form onSubmit={handleSubmit}>
                    <Grid container gap={8}>
                        <Grid item xs={12}>
                            <TextField name='email' label='Email' fullWidth autoFocus onChange={()=>{ setError(null); }}/>
                        </Grid>
                        { error &&
                            <Grid item xs={12}>
                                <Typography color="error" variant='body2'>{error}</Typography>
                            </Grid>
                        }

                        <Grid item xs={12}>
                            <Button type="submit" variant="contained" size="large" fullWidth>Request Password Reset</Button> 
                        </Grid>
                    </Grid>
                </form>;
    } else {
        return <form onSubmit={handleSubmit}>
                    <Grid container gap={8}>
                        <Grid item xs={8}>
                            <Typography>Enter code from email</Typography>
                            <TextField name='passcode' label='Passcode' fullWidth autoFocus onChange={()=>{ setError(null); }}/>
                        </Grid>
                        <Grid item xs={8}>
                            <Typography>Choose new password</Typography>
                            <TextField name='newPassword' label='New Password' fullWidth type="password" />
                            <TextField name='passwordConfirmation' label='Repeat Password' fullWidth type="password" />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography>Didn&apos;t recieve a code?</Typography>
                            <Button variant="outlined" onClick={resendVerificationCode}>Try again</Button>
                        </Grid>
                        { error &&
                            <Grid item xs={12}>
                                <Typography color="error" variant='body2'>{error}</Typography>
                            </Grid>
                        }

                        <Grid item xs={12}>
                            <Button type="submit" variant="contained" size="large" fullWidth>Reset Password</Button> 
                        </Grid>
                    </Grid>
                </form>;
    }
  }

  function onCloseErrorDialog() {
      setPageError("");
  }

  return (
    <AuthLayout>
      <Box maxWidth={'sm'}>
        <Box padding={theme.spacing(8)}>
        <Typography variant="h5" component="h2" align="center" mb={8} fontWeight="600">{getTitle()}</Typography>
        {getInputForm()}

        <Grid container mt={4} color={theme.palette.grey['600']}>
            <Grid item xs={12} textAlign="center">
            <Typography variant="body2" mt={2}>Already registered? <Link to="/login">Log in</Link></Typography>
            </Grid>
        </Grid>
        </Box>
      </Box>
      <FinishResetDialog open={displayFinishAlert} />
      <ErrorDialog errorMessage={pageError} open={pageError.length > 0} onClose={onCloseErrorDialog} />
    </AuthLayout>
  );
}

export default ResetPassword;